import { useMemo, useState } from "react"

import type { MetricDashboardSource } from "@/metrics/catalog"

import type { ChartWidgetConfig } from "../../types/chart"
import { getWidgetMetricFilters } from "../../utils/widgetMetricFilterValidation"
import {
  getWidgetMetricOutputType,
  isWidgetMetricOutputType,
} from "../../utils/widgetMetricOutputType"

function withoutHiddenSeries(config: ChartWidgetConfig): string {
  return JSON.stringify({ ...config, hiddenSeries: undefined })
}

/**
 * The config to derive chart data from. Its identity changes with every
 * config edit except `hiddenSeries`, which only affects rendering, so a legend
 * toggle in the widget builder keeps the chart data array stable.
 */
export function useChartDataConfig(
  config: ChartWidgetConfig,
): ChartWidgetConfig {
  const [current, setCurrent] = useState(config)
  if (
    current !== config &&
    withoutHiddenSeries(current) !== withoutHiddenSeries(config)
  ) {
    setCurrent(config)
    return config
  }
  return current
}

export function useResolvedChartConfig(
  savedConfig: ChartWidgetConfig,
  metric: MetricDashboardSource | null | undefined,
): ChartWidgetConfig {
  const outputType = metric ? getWidgetMetricOutputType(metric) : undefined
  return useMemo(
    () => ({
      ...savedConfig,
      metricOutputType: isWidgetMetricOutputType(outputType)
        ? outputType
        : savedConfig.metricOutputType,
      metricFilter: getWidgetMetricFilters(savedConfig.metricFilter),
    }),
    [savedConfig, outputType],
  )
}
